import styled from "styled-components/macro";
import {useCallback, useState} from "react";
import {useParams} from "react-router-dom";
import {call} from "redux-saga/effects";
import Header from "../../components/header";
import TopBar from "../../components/top_bar";
import SortBar from "../../components/sort_bar";
import CommunityHeader from "../../components/community_header";
import Container from "../../components/container";
import Main from '../../components/main';
import Sidebar from "../../components/sidebar";
import Footer from "../../components/footer";
import {useFeedback} from "../../store/feedback";
import Wrapper from "../../components/wrapper";
import {useCommunityStyle} from "../../api/communities";
import {useBaseCommunity} from "../../store/hooks";
import HeaderData from "../../components/header_data";
import NavLink from "../../components/nav_link";
import {useQuery} from "../../utils/router";
import {getMessage, useCreateReply} from "../../api/message";
import Message from "../../components/message";
import MarkdownField from "../../components/markdown_field";
import Button from "../../components/button";
import ErrorMessage from "../../components/error_message";
import {useSagaEffect} from "../../store/sagas";
import {useModal} from "../../store";
import CreateMessage from "../../components/create_message";
import {isPseudoCommunity} from "../../store/communites";
import {themecolors} from "../../utils/themecolors";
import LegacySortBar from "../../components/legacy/legacy_sort_bar";
import useWindowDimensions from "../../utils/dimensions";

const Items = styled.div``;
const Item = styled.div``;
const Manage = styled.div``;
const Save = styled.div``;
const Form = styled.div``;
const Messages = styled.div``;
const New = styled.div``;

const MessageThread = styled(({className}) => {


    const community = useBaseCommunity();
    const style = useCommunityStyle(community);
    const {id} = useParams();

    const {type = "all"} = useQuery();
    const loadFeedback = useFeedback();
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState('');
    useSagaEffect(loadFeedback.channel, useCallback(function* () {
        setMessages(yield call(getMessage, id));
    }, [id]));

    const replyFeedback = useFeedback();
    const createReply = useCreateReply(replyFeedback.channel);
    const handleSend = async () => {
        try {
            const response = await createReply(id, content);
            setMessages((messages) => [...messages, ...response.messages]);
            setContent('');
        } catch {
            // error is displayed
        }
    };

    const modal = useModal();
    const handleMessage = () => {
        modal.open(CreateMessage);
    };

    const {width} = useWindowDimensions();
    
    return (
        <div className={className}>
            <HeaderData title='Message Thread' community={community} community_style={style}/>
            <Header>
                {(community !== "TheDonald" || width <= 780) && <TopBar legacyConversion={community === "TheDonald"} page={'Message'}/>}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                    </>
                }
                {community === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community}/>
            <Container>
                <Wrapper>
                    <Manage>
                        <Items>
                            <Item className={(!type || type === 'all') && 'active'}>
                                <NavLink href={`/messages?type=all`} react_route>All</NavLink>
                            </Item>
                            <Item className={type === 'unread' && 'active'}>
                                <NavLink href={`/messages?type=unread`} react_route>Unread</NavLink>
                            </Item>
                            <Item className={type === 'messages' && 'active'}>
                                <NavLink href='/messages?type=messages' react_route>Messages</NavLink>
                            </Item>
                            <Item className={type === 'comments' && 'active'}>
                                <NavLink href='/messages?type=comments' react_route>Comments</NavLink>
                            </Item>
                            <Item className={type === 'sent' && 'active'}>
                                <NavLink href='/messages?type=sent' react_route>Sent</NavLink>
                            </Item>
                            <Item onClick={handleMessage}>
                                Create Message
                            </Item>
                        </Items>
                    </Manage>
                    <Main>
                        <div>
                            <Messages>
                                {messages?.slice(0).reverse().map((message) => (
                                    <Message
                                        key={message.id}
                                        message={message}
                                        showSubject={message.id === message.conversation_id}
                                        hideActions
                                        scrollIntoView={message === messages[0]}
                                    />
                                ))}
                            </Messages>
                            {messages?.length > 0 && messages[0].type !== 5 && messages[0].type !== 2 && (
                                <Form>
                                    <MarkdownField
                                        id='content' placeholder="Add your message" minRows={5}
                                        value={content} onChange={setContent} autofocus={"true"}
                                    />
                                    <Save>
                                        <Button onClick={handleSend} disabled={replyFeedback.busy}>Send</Button>
                                    </Save>
                                    <ErrorMessage error={replyFeedback.error}/>
                                </Form>
                            )}
                        </div>
                    </Main>
                </Wrapper>
                <Sidebar sidebar={style && style.sidebar} community={community} />
            </Container>
            <Footer/>
        </div>
    );
})`
  ${Main} {
    font-size: 14px;
    padding-bottom: 4px;
  
  }

  ${Form} {
    width: 464px;
    max-width: 100%;
    margin: 16px 0;
  }

  ${Form} ${MarkdownField} {
    max-width: calc(100% - 24px);
    margin: 0 auto;
  }

  ${Save} {
    margin: 8px 12px;
  }

  ${Save} button {
    display: flex;
    margin-left: auto;
    margin-bottom: 12px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.dark_field : themecolors.pure_white};
    border: ${({theme}) => theme.color.name === 'dark' ? `1px solid ${theme.dark_border}` : '1px solid #ddd'};
    padding: 8px 24px;
  }

  ${Messages} {
    display: flex;
    flex-direction: column-reverse;
  }
  ${Message} {
    border-bottom: ${({theme}) => theme.color.name === "dark" ? `1px solid ${themecolors.dark_background_5}` : theme.standard_border};
  }

 ${Item} {
    padding: 6px 16px;
    margin-right: 6px;
    border-radius: 48px;
    white-space: nowrap;
    cursor: pointer;
  }
  ${Items} {
    display: flex;
    align-items: center;
  }

  ${Manage} {
    display: flex;
    font-size: 15px;
    margin-bottom: 12px;
  }

  ${Item} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${Item}.active {
    background-color: ${({theme}) => theme.color.name === "dark" ? themecolors.dark_background_2 : '#fff'};
  }
  
  ${New} {
    cursor: pointer;
    margin-left: 6px;
    margin-right: 32px;
  }

  @media only screen and (max-width: 780px) {
    ${Manage} {
      display: initial;
    }
    ${Items} {
      padding: 8px;
      overflow-y: scroll;
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
    ${Items}::-webkit-scrollbar {
      display: none;
    }
    ${New} {
      margin-left: 0;
      margin-right: 12px;
    }
  }
  
  }
`;

export default MessageThread;
